import React, { useMemo, useState, useCallback } from 'react';
import { IconChevronRight, IconLibraryMinus, IconLibraryPlus, IconCopy, IconCheck } from '@tabler/icons-react';
import { Box, Group, ActionIcon, Tree, Text, Code, Badge, getTreeExpandedState, useTree } from '@mantine/core';
import { convertToTreeData, isExpandable, formatValue } from './utils';
import classes from './JsonTree.module.css';

const defaultProps = {
  defaultExpanded: false,
  maxDepth: 2,
  withExpandAll: false,
  showItemsCount: false,
  showIndentGuides: false,
  stickyHeader: false,
  displayFunctions: 'as-string',
};

/**
 * JsonTree component - A custom JSON tree viewer with text selection and copy support
 */
const JsonTree = (_props) => {
  const props = { ...defaultProps, ..._props };
  const {
    data,
    defaultExpanded,
    maxDepth,
    onNodeClick,
    onCopy,
    withExpandAll,
    title,
    showItemsCount,
    showIndentGuides,
    stickyHeader,
    displayFunctions,
    expandAllControlIcon = <IconLibraryPlus size={16} />,
    collapseAllControlIcon = <IconLibraryMinus size={16} />,
    expandControlIcon,
    collapseControlIcon,
    ...others
  } = props;

  const [copiedPath, setCopiedPath] = useState(null);

  const treeData = useMemo(() => [convertToTreeData(data, undefined, 'root', 0, displayFunctions)], [data, displayFunctions]);

  const initialExpandedState = useMemo(() => {
    if (defaultExpanded) {
      if (maxDepth === -1) {
        return getTreeExpandedState(treeData, '*');
      }
      const expandedNodes = [];
      const traverse = (nodes, depth) => {
        nodes.forEach((node) => {
          if (depth < maxDepth && node.children) {
            expandedNodes.push(node.value);
            traverse(node.children, depth + 1);
          }
        });
      };
      traverse(treeData, 0);
      return getTreeExpandedState(treeData, expandedNodes);
    }
    return {};
  }, [treeData, defaultExpanded, maxDepth]);

  const tree = useTree({
    initialExpandedState,
  });

  const handleCopy = useCallback(
    async (e, value, path, type) => {
      e.stopPropagation();
      try {
        // For primitive strings, copy without quotes
        const copy = type === 'string' ? value : JSON.stringify(value, null, 2);
        await navigator.clipboard.writeText(copy);
        setCopiedPath(path);
        setTimeout(() => setCopiedPath(null), 1500);
        onCopy?.(copy, value);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    },
    [onCopy]
  );

  function renderJSONNode({ node, expanded, hasChildren, elementProps, tree: treeInstance }) {
    const jsonNode = node;
    const { type, value, key, path, itemCount, depth = 0 } = jsonNode.nodeData || {
      type: 'null',
      value: null,
      path: 'unknown',
      depth: 0,
    };

    const handleClick = () => {
      if (onNodeClick) {
        onNodeClick(path, value);
      }
    };

    const renderIndentGuides = () => {
      if (!showIndentGuides || depth === 0) {
        return null;
      }
      const guides = [];
      for (let i = 0; i < depth; i++) {
        const colorIndex = i % 5;
        guides.push(
          <div
            key={i}
            className={classes.indentGuide}
            style={{ left: `${i * 32 + 8}px` }}
            data-color-index={colorIndex}
          />
        );
      }
      return guides;
    };

    const renderCopyButton = () => {
      if (copiedPath === path) {
        return <IconCheck size={14} className={classes.copiedIcon} />;
      }

      return (
        <IconCopy
          size={14}
          className={classes.copyIcon}
          onClick={(e) => handleCopy(e, value, path, type)}
        />
      );
    };

    if (!hasChildren) {
      return (
        <Group
          gap={4}
          wrap="nowrap"
          onClick={handleClick}
          className={classes.nodeRow}
          style={{ cursor: onNodeClick ? 'pointer' : 'default', position: 'relative' }}
        >
          {renderIndentGuides()}
          {/* Spacer to align leaf node text with expandable node text (after chevron) - matches ActionIcon size="xs" */}
          <span style={{ width: 20, flexShrink: 0 }} />
          {key !== undefined && (
            <>
              <Text component="span" className={classes.key} data-key={key}>
                {key}
              </Text>
              <Text component="span" className={classes.keyValueSeparator}>
                :
              </Text>
            </>
          )}
          {(() => {
            const formattedValue = formatValue(value, type);
            return (
              <Code className={classes.value} data-type={type} data-value={formattedValue}>
                {formattedValue}
              </Code>
            );
          })()}
          {renderCopyButton()}
        </Group>
      );
    }

    const openBracket = type === 'array' ? '[' : '{';
    const closeBracket = type === 'array' ? ']' : '}';

    const expandCollapseIcon = (() => {
      if (!expandControlIcon && !collapseControlIcon) {
        return (
          <IconChevronRight
            size={14}
            style={{
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        );
      }
      if (expandControlIcon && !collapseControlIcon) {
        return React.cloneElement(expandControlIcon, {
          style: {
            ...expandControlIcon.props?.style,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          },
        });
      }
      if (!expandControlIcon && collapseControlIcon) {
        return expanded ? collapseControlIcon : <IconChevronRight size={14} />;
      }
      return expanded ? collapseControlIcon : expandControlIcon;
    })();

    return (
      <Group
        gap={4}
        wrap="nowrap"
        onClick={handleClick}
        className={classes.nodeRow}
        data-expanded={expanded}
        data-has-children={hasChildren}
        data-type={type}
        style={{ cursor: onNodeClick ? 'pointer' : 'default', position: 'relative' }}
      >
        {renderIndentGuides()}
        <ActionIcon
          size="xs"
          variant="subtle"
          onClick={(e) => {
            e.stopPropagation();
            treeInstance.toggleExpanded(node.value);
          }}
        >
          {expandCollapseIcon}
        </ActionIcon>
        {key !== undefined && (
          <>
            <Text component="span" className={classes.key}>
              {key}
            </Text>
            <Text component="span" className={classes.keyValueSeparator}>
              :
            </Text>
          </>
        )}
        <Text component="span" className={classes.bracket}>
          {openBracket}
        </Text>
        {!expanded && (
          <>
            <Text component="span" size="xs" className={classes.ellipsis}>
              ...
            </Text>
            <Text component="span" className={classes.bracket}>
              {closeBracket}
            </Text>
            {itemCount !== undefined && showItemsCount && (
              <Badge size="xs" variant="light" color="gray">
                {itemCount}
              </Badge>
            )}
          </>
        )}
        {renderCopyButton()}
      </Group>
    );
  }

  return (
    <Box ref={others.ref} className={classes.root} {...others}>
      {(title || withExpandAll) && (
        <Group className={classes.header} justify="space-between" data-sticky={stickyHeader}>
          {title || <div />}
          {withExpandAll && isExpandable(data) && (
            <Group gap="xs" style={{ top: 10, zIndex: 1 }}>
              <ActionIcon size="xs" variant="transparent" onClick={() => tree.expandAllNodes()}>
                {expandAllControlIcon}
              </ActionIcon>
              <ActionIcon size="xs" variant="transparent" onClick={() => tree.collapseAllNodes()}>
                {collapseAllControlIcon}
              </ActionIcon>
            </Group>
          )}
        </Group>
      )}
      <Tree data={treeData} tree={tree} levelOffset={0} renderNode={(payload) => renderJSONNode(payload)} />
    </Box>
  );
};

JsonTree.displayName = 'JsonTree';

export { JsonTree };
export default JsonTree;
